import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function CheckoutPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const URL = process.env.REACT_APP_SERVER_URL;
  const selectedPlan = location.state?.selectedPlan;

  // 🔒 Redirigir si no hay plan seleccionado
  useEffect(() => {
    if (!selectedPlan) {
      navigate("/subscription");
    }
  }, [selectedPlan, navigate]);

  // ✅ Esperar SDK de Onvo
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.onvo && typeof window.onvo.pay === "function") {
        setSdkReady(true);
        clearInterval(interval);
        console.log("✅ SDK de Onvo cargado correctamente.");
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // 🧩 Inicializar pago cuando el SDK esté listo
  useEffect(() => {
    if (sdkReady && selectedPlan) {
      initializePayment();
    }
  }, [sdkReady, selectedPlan]);

  const initializePayment = async () => {
    setLoading(true);
    setError("");

    try {
      console.log("🌀 Creando suscripción para:", selectedPlan.id);

      // 1️⃣ Crear suscripción en backend
      const res = await fetch(`${URL}/api/authOperator/subscriptions/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ planId: selectedPlan.id }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Error al crear la suscripción");

      const { subscriptionId, customerId } = data;
      if (!subscriptionId || !customerId) {
        throw new Error("El backend no devolvió los IDs necesarios.");
      }

      // 2️⃣ Renderizar formulario de pago
      const onvoInstance = window.onvo.pay({
        publicKey: process.env.REACT_APP_ONVO_PUBLIC_KEY,
        paymentType: "subscription",
        subscriptionId,
        customerId,
        onSuccess: async (result) => {
          // 3️⃣ Confirmar en backend
          if (result.status === "active") {
            await fetch(`${URL}/api/authOperator/subscriptions/confirm`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                result,
                subscriptionId: result.id,
                customerId: result.customerId,
                paymentMethodId: result.paymentMethodId,
                status: result.status,
              }),
            });

            alert("🎉 Suscripción activa. ¡Bienvenido! inicia sesión");
            navigate("/login");
          } else {
            alert("⚠️ El pago fue procesado, pero requiere verificación.");
          }
        },
        onError: (error) => {
          console.error("❌ Error en el pago:", error);
          setError("Error al procesar el pago. Verifique la tarjeta o intente de nuevo.");
        },
      });

      onvoInstance.render("#onvo-container");
    } catch (err) {
      console.error("🚨 Error al suscribirse:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedPlan) {
    return null; // O un loading spinner
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50">
      <button
        onClick={() => navigate("/subscription")}
        className="self-start mb-4 flex items-center text-blue-600 hover:text-blue-800"
      >
        <ArrowLeft size={20} className="mr-2" />
        Volver a planes
      </button>

      <motion.h1
        className="text-3xl font-bold mb-2 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        Completar Suscripción
      </motion.h1>
      
      <motion.p 
        className="text-lg text-gray-600 mb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        Plan: <strong>{selectedPlan.name}</strong>
      </motion.p>

      {error && (
        <div className="bg-red-100 text-red-700 border border-red-300 px-4 py-2 rounded mb-4 max-w-md w-full">
          {error}
        </div>
      )}

      <div className="w-full max-w-md">
        {loading && (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="animate-spin mr-2" size={24} />
            <span>Preparando formulario de pago...</span>
          </div>
        )}
        
        <div
          id="onvo-container"
          className={`transition-all ${sdkReady ? "opacity-100" : "opacity-0"}`}
        ></div>
      </div>
    </div>
  );
}